import { DEFAULT_STORE_OBJECT } from '@/redux/constants';
import type { DefaultStoreObject } from '@/redux/types';
import type {
  OverChargeClaim,
  OverChargeClaimComment,
  OverChargeClaimDetailed,
  OverChargeClaimEmail,
  OverChargeClaimItem,
  OverChargeClaimSettlement,
  OverChargeSearchFilters,
} from '@/views/screens/Claims/types';
import { cloneDeep } from 'es-toolkit';
import { OVERCHARGE_CLAIMS_FILTERS_INITIAL_DATA } from '../../../contants/ClaimsEnums';
import { SEARCH_VIEW_TYPES, type SearchViewType } from '../../../contants/common';
import OverChargeClaimsTypes from './OverChargeTypes';

export interface OverChargeClaimsState {
  claimsFilters: OverChargeSearchFilters;
  claimsList: DefaultStoreObject<OverChargeClaim[]> & {
    openClaim: number;
    totalRecords: number;
    closedClaim: number;
    holdClaim: number;
    filedAmount: number;
    paidAmount: number;
    isFirstTime: boolean;
  };
  editDrawer: boolean;
  selectedClaims: OverChargeClaim['fbocKey'][];
  claimDetails: DefaultStoreObject<OverChargeClaimDetailed>;
  editTabKey: string;
  createClaim: DefaultStoreObject<null>;
  claimViewMode: SearchViewType;
  tableViewColumns: DefaultStoreObject<
    {
      columnName: string;
      columnValue: string;
    }[]
  >;
  sortingFields: DefaultStoreObject<
    {
      key: string;
      value: string;
    }[]
  >;
  saveTableViewColumns: DefaultStoreObject<
    {
      columnName: string;
      columnValue: string;
      id: string;
    }[]
  >;
  claimNotes: DefaultStoreObject<OverChargeClaimComment[]> & {
    totalRows?: number;
  };
  claimSettlements: DefaultStoreObject<OverChargeClaimSettlement[]> & {
    totalRows?: number;
    totalAmount?: number;
  };
  applyClaimRecoveryPercent: DefaultStoreObject<null>;
  claimItems: DefaultStoreObject<OverChargeClaimItem[]> & {
    totalRows?: number;
    totalBilledAmount?: number;
    totalPaidAmount?: number;
    totalCorrectCharges?: number;
    totalOverchargeAmount?: number;
    totalRecoveryAmount?: number;
  };
  sendPdfToCarrier: DefaultStoreObject<number>;
  claimEmailHistory: DefaultStoreObject<OverChargeClaimEmail[]>;
  itemsFromExcel: DefaultStoreObject<unknown>;
}

const initialState: OverChargeClaimsState = {
  claimsFilters: OVERCHARGE_CLAIMS_FILTERS_INITIAL_DATA,
  claimsList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    totalRecords: 0,
    openClaim: 0,
    closedClaim: 0,
    holdClaim: 0,
    filedAmount: 0,
    paidAmount: 0,
    isFirstTime: true,
  },
  editDrawer: false,
  selectedClaims: [],
  claimDetails: {
    ...DEFAULT_STORE_OBJECT,
    data: {} as OverChargeClaimDetailed,
  },
  editTabKey: '1',
  sendPdfToCarrier: {
    ...DEFAULT_STORE_OBJECT,
  },
  claimEmailHistory: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  itemsFromExcel: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
  },
  createClaim: {
    ...DEFAULT_STORE_OBJECT,
  },
  claimViewMode: SEARCH_VIEW_TYPES.CARD,
  tableViewColumns: { ...DEFAULT_STORE_OBJECT, data: [] },
  sortingFields: { ...DEFAULT_STORE_OBJECT, data: [] },
  saveTableViewColumns: { ...DEFAULT_STORE_OBJECT, data: [] },
  claimNotes: { ...DEFAULT_STORE_OBJECT, data: [], totalRows: 0 },
  claimSettlements: { ...DEFAULT_STORE_OBJECT, data: [], totalAmount: 0 },
  claimItems: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    totalRows: 0,
    totalBilledAmount: 0,
    totalPaidAmount: 0,
    totalCorrectCharges: 0,
    totalOverchargeAmount: 0,
    totalRecoveryAmount: 0,
  },
  applyClaimRecoveryPercent: DEFAULT_STORE_OBJECT,
};
// biome-ignore lint/style/useDefaultParameterLast: <explanation>
const OverChargeClaimsReducer = (state = initialState, { type, payload }: any): OverChargeClaimsState => {
  switch (type) {
    case OverChargeClaimsTypes.SET_OVERCHARGE_FILTERS: {
      return {
        ...state,
        claimsFilters: {
          ...state.claimsFilters,
          ...payload,
        },
      };
    }

    case OverChargeClaimsTypes.SET_OVERCHARGE_CLAIM_EDIT_ACTIVE_KEY: {
      return {
        ...state,
        editTabKey: payload,
      };
    }
    case OverChargeClaimsTypes.GET_OVERCHARGE_LIST_PROGRESS: {
      return {
        ...state,
        claimsList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.claimsList,
          progressing: true,
          isFirstTime: false,
        },
      };
    }
    case OverChargeClaimsTypes.GET_OVERCHARGE_LIST_SUCCESS: {
      let selectedClaims = cloneDeep(state.selectedClaims);
      if (
        state.selectedClaims?.length === state.claimsList.data?.length &&
        state.selectedClaims?.length !== 0 // To fixe select all checkbox issue
      ) {
        // @ts-ignore
        payload.data.claimsData.forEach(({ fbocKey }) => selectedClaims.push(fbocKey));
      } else if (state.claimsFilters.pageNo === 1) {
        selectedClaims = []; // To solve shipment headers issue
      }
      return {
        ...state,
        selectedClaims,
        claimsList: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: [...(state.claimsList.data || []), ...payload.data.claimsData],

          // Have to reflect the counts only if SelectedValue is empty
          // ...(state.shipmentFilters.SelectedValue.length === 0
          //   ? {

          //     }
          //   : {}),
          totalRecords: payload.data.totalClaim,
          openClaim: payload.data.openClaim,
          closedClaim: payload.data.closedClaim,
          holdClaim: payload.data.holdClaim,
          filedAmount: payload.data.filedAmount,
          paidAmount: payload.data.paidAmount,
          isFirstTime: false,
        },
      };
    }
    case OverChargeClaimsTypes.GET_OVERCHARGE_LIST_ERROR: {
      return {
        ...state,
        claimsList: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [...(state.claimsList.data || [])],
          isFirstTime: false,
        },
      };
    }
    case OverChargeClaimsTypes.SET_OVERCHARGE_LIST: {
      return {
        ...state,
        claimsList: {
          ...DEFAULT_STORE_OBJECT,
          // totalRecords: 0,
          ...state.claimsList,
          ...payload,
        },
      };
    }

    case OverChargeClaimsTypes.SET_OVERCHARGE_EDIT_DRAWER: {
      return {
        ...state,
        editDrawer: payload,
      };
    }

    case OverChargeClaimsTypes.SET_SELECTED_OVERCHARGE_CLAIMS: {
      return {
        ...state,
        selectedClaims: payload,
      };
    }

    case OverChargeClaimsTypes.GET_OVERCHARGE_CLAIM_DETAILS_PROGRESS: {
      return {
        ...state,
        claimDetails: {
          ...DEFAULT_STORE_OBJECT,
          data: {} as OverChargeClaimDetailed,
          progressing: true,
        },
        claimNotes: { ...state.claimNotes, data: [] },
        claimSettlements: { ...state.claimSettlements, data: [] },
        claimItems: {
          ...state.claimItems,
          data: [],
          totalRows: 0,
          totalBilledAmount: 0,
          totalPaidAmount: 0,
          totalCorrectCharges: 0,
          totalOverchargeAmount: 0,
          totalRecoveryAmount: 0,
        },
      };
    }
    case OverChargeClaimsTypes.GET_OVERCHARGE_CLAIM_DETAILS_SUCCESS: {
      return {
        ...state,
        claimDetails: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case OverChargeClaimsTypes.GET_OVERCHARGE_CLAIM_DETAILS_ERROR: {
      return {
        ...state,
        claimDetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case OverChargeClaimsTypes.SEND_CLAIM_PDF_TO_CARRIER_PROGRESS: {
      return {
        ...state,
        sendPdfToCarrier: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case OverChargeClaimsTypes.SEND_CLAIM_PDF_TO_CARRIER_SUCCESS: {
      return {
        ...state,
        sendPdfToCarrier: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case OverChargeClaimsTypes.SEND_CLAIM_PDF_TO_CARRIER_ERROR: {
      return {
        ...state,
        sendPdfToCarrier: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case OverChargeClaimsTypes.GET_CLAIM_EMAIL_HISTORY_PROGRESS: {
      return {
        ...state,
        claimEmailHistory: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          progressing: true,
        },
      };
    }
    case OverChargeClaimsTypes.GET_CLAIM_EMAIL_HISTORY_SUCCESS: {
      return {
        ...state,
        claimEmailHistory: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case OverChargeClaimsTypes.GET_CLAIM_EMAIL_HISTORY_ERROR: {
      return {
        ...state,
        claimEmailHistory: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
          ...payload,
        },
      };
    }

    case OverChargeClaimsTypes.GET_ITEMS_FROM_EXCEL_PROGRESS: {
      return {
        ...state,
        itemsFromExcel: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          progressing: true,
        },
      };
    }
    case OverChargeClaimsTypes.GET_ITEMS_FROM_EXCEL_SUCCESS: {
      return {
        ...state,
        itemsFromExcel: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case OverChargeClaimsTypes.GET_ITEMS_FROM_EXCEL_ERROR: {
      return {
        ...state,
        itemsFromExcel: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
          ...payload,
        },
      };
    }

    case OverChargeClaimsTypes.CREATE_OVERCHARGE_CLAIM_PROGRESS: {
      return {
        ...state,
        createClaim: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case OverChargeClaimsTypes.CREATE_OVERCHARGE_CLAIM_SUCCESS: {
      return {
        ...state,
        createClaim: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case OverChargeClaimsTypes.CREATE_OVERCHARGE_CLAIM_ERROR: {
      return {
        ...state,
        createClaim: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }
    case OverChargeClaimsTypes.CREATE_OVERCHARGE_CLAIM_RESET: {
      return {
        ...state,
        createClaim: {
          ...DEFAULT_STORE_OBJECT,
          data: null,
          success: false,
        },
      };
    }
    case OverChargeClaimsTypes.SET_CLAIM_SEARCH_VIEW_TYPE_INITIAL:
    case OverChargeClaimsTypes.SET_CLAIM_SEARCH_VIEW_TYPE: {
      return {
        ...state,
        claimViewMode: payload,
      };
    }

    case OverChargeClaimsTypes.GET_TABLE_VIEW_COLUMNS_PROGRESS: {
      return {
        ...state,
        tableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case OverChargeClaimsTypes.GET_TABLE_VIEW_COLUMNS_SUCCESS: {
      return {
        ...state,
        tableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case OverChargeClaimsTypes.GET_TABLE_VIEW_COLUMNS_ERROR: {
      return {
        ...state,
        tableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
        },
      };
    }

    case OverChargeClaimsTypes.GET_SORTING_FIELDS_PROGRESS: {
      return {
        ...state,
        sortingFields: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case OverChargeClaimsTypes.GET_SORTING_FIELDS_SUCCESS: {
      return {
        ...state,
        sortingFields: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case OverChargeClaimsTypes.GET_SORTING_FIELDS_ERROR: {
      return {
        ...state,
        sortingFields: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
        },
      };
    }

    case OverChargeClaimsTypes.SAVE_TABLE_VIEW_COLUMNS_PROGRESS: {
      return {
        ...state,
        saveTableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: [],
        },
      };
    }
    case OverChargeClaimsTypes.SAVE_TABLE_VIEW_COLUMNS_SUCCESS: {
      return {
        ...state,
        saveTableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          ...payload,
        },
      };
    }
    case OverChargeClaimsTypes.SAVE_TABLE_VIEW_COLUMNS_ERROR: {
      return {
        ...state,
        saveTableViewColumns: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: [],
        },
      };
    }

    case OverChargeClaimsTypes.GET_CLAIM_NOTES_PROGRESS: {
      return {
        ...state,
        claimNotes: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: payload.pageIndex === 1 ? [] : state.claimNotes.data,
        },
      };
    }
    case OverChargeClaimsTypes.GET_CLAIM_NOTES_SUCCESS: {
      return {
        ...state,
        claimNotes: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: !payload.removeOld ? state.claimNotes.data?.concat(payload.data) : payload.data,
          totalRows: payload.totalRows,
        },
      };
    }
    case OverChargeClaimsTypes.GET_CLAIM_NOTES_ERROR: {
      return {
        ...state,
        claimNotes: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: state.claimNotes.data,
          totalRows: state.claimNotes.totalRows,
          ...payload,
        },
      };
    }

    case OverChargeClaimsTypes.GET_CLAIM_SETTLEMENTS_PROGRESS: {
      return {
        ...state,
        claimSettlements: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: payload.pageIndex === 1 ? [] : state.claimSettlements.data,
          totalAmount: state.claimSettlements.totalAmount,
        },
      };
    }
    case OverChargeClaimsTypes.GET_CLAIM_SETTLEMENTS_SUCCESS: {
      return {
        ...state,
        claimSettlements: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: !payload.removeOld ? state.claimSettlements.data?.concat(payload.data) : payload.data,
          totalRows: payload.totalRows,
          totalAmount: payload.totalAmount,
        },
      };
    }
    case OverChargeClaimsTypes.GET_CLAIM_SETTLEMENTS_ERROR: {
      return {
        ...state,
        claimSettlements: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: state.claimSettlements.data,
          totalRows: state.claimSettlements.totalRows,
          ...payload,
        },
      };
    }

    case OverChargeClaimsTypes.GET_CLAIM_ITEMS_PROGRESS: {
      return {
        ...state,
        claimItems: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
          data: payload.pageIndex === 1 ? [] : state.claimItems.data,
        },
      };
    }
    case OverChargeClaimsTypes.GET_CLAIM_ITEMS_SUCCESS: {
      return {
        ...state,
        claimItems: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
          data: !payload.removeOld ? state.claimItems.data?.concat(payload.data) : payload.data,
          totalRows: payload.totalRows,
          totalBilledAmount: payload.totalBilledAmount,
          totalPaidAmount: payload.totalPaidAmount,
          totalCorrectCharges: payload.totalCorrectCharges,
          totalOverchargeAmount: payload.totalOverchargeAmount,
          totalRecoveryAmount: payload.totalRecoveryAmount,
        },
      };
    }
    case OverChargeClaimsTypes.GET_CLAIM_ITEMS_ERROR: {
      return {
        ...state,
        claimItems: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          data: state.claimItems.data,
          totalRows: state.claimItems.totalRows,
          ...payload,
        },
      };
    }
    case OverChargeClaimsTypes.APPLY_CLAIM_RECOVERY_PERCENT_PROGRESS: {
      return {
        ...state,
        applyClaimRecoveryPercent: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }
    case OverChargeClaimsTypes.APPLY_CLAIM_RECOVERY_PERCENT_SUCCESS: {
      return {
        ...state,
        applyClaimRecoveryPercent: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
        },
      };
    }
    case OverChargeClaimsTypes.APPLY_CLAIM_RECOVERY_PERCENT_ERROR: {
      return {
        ...state,
        applyClaimRecoveryPercent: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
        },
      };
    }
    default:
      return state;
  }
};

export default OverChargeClaimsReducer;
